//
//  Hierarchy.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: October 24, 2026
//  Author: Bob Keathley
//
//  The tree Corporate → Teams → Projects with the people at each node (§6v).
//  Create a team, move a project into it, attach a person.
//
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { api } from '@shared/api/client'
import { isOk } from '@shared/api/envelope'
import { canEditHierarchy, levelErrorText, type LevelTree, type NotebookRef, type Person, type PersonRow, type TreeProject } from '@shared/api/levels'
import type { Session } from '@shared/auth'
import { href } from '@shared/router'

function NotebookPill({ nb }: { nb: NotebookRef | null }) {
  return nb ? <span className="pill ok">v{nb.version}</span> : <span className="pill off">no Notebook</span>
}

function People({ people: raw }: { people?: Person[] | null }) {
  const people = raw ?? []
  if (people.length === 0) return <span className="hint">no people attached</span>
  return <>{people.map((p) => <a key={p.user_id} className="chip" href={href(`/hierarchy/users/${p.user_id}`)} title={p.email}>{p.name || p.email}</a>)}</>
}

function ProjectRow({ p, teams, teamId, editable, onMove, onRemove }: {
  p: TreeProject; teams: LevelTree['teams']; teamId: string | null; editable: boolean
  onMove: (teamId: string, projectId: string) => void; onRemove: (teamId: string, projectId: string) => void
}) {
  return (
    <li className="node project">
      <div className="head">
        <a href={href(`/hierarchy/projects/${p.id}`)}>{p.name}</a> <NotebookPill nb={p.notebook} />{' '}
        <a className="hint" href={href(`/projects/${p.id}`)}>project page</a>
        {editable && (
          <span className="acts">
            <select aria-label={`Move ${p.name} into a team`} value="" onChange={(e) => e.target.value && onMove(e.target.value, p.id)}>
              <option value="">Move into team…</option>
              {teams.filter((t) => t.id !== teamId).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {teamId && <button className="btn ghost sm" onClick={() => onRemove(teamId, p.id)}>Back to Corporate</button>}
          </span>
        )}
      </div>
      <div className="people"><People people={p.people} /></div>
    </li>
  )
}

function AttachRow({ row, tree, token, onChanged, onError }: { row: PersonRow; tree: LevelTree; token: string; onChanged: () => void; onError: (m: string) => void }) {
  const current = row.attachment ? `${row.attachment.level}:${row.attachment.ref_id ?? ''}` : ''
  const [target, setTarget] = useState(current)
  const [busy, setBusy] = useState(false)
  useEffect(() => setTarget(current), [current])
  const projects = [...(tree.teams ?? []).flatMap((t) => t.projects ?? []), ...(tree.unassigned_projects ?? [])]
  async function attach() {
    const [level, ref] = target.split(':')
    setBusy(true)
    const r = await api<unknown>(`/levels/people/${row.user_id}/attachment`, { method: 'PUT', body: JSON.stringify({ level, ref_id: level === 'corporate' ? null : ref }) }, token)
    setBusy(false)
    if (!isOk(r)) { onError(levelErrorText(r.error)); return }
    onChanged()
  }
  async function detach() {
    setBusy(true)
    const r = await api<unknown>(`/levels/people/${row.user_id}/attachment`, { method: 'DELETE' }, token)
    setBusy(false)
    if (!isOk(r)) { onError(levelErrorText(r.error)); return }
    onChanged()
  }
  return (
    <tr>
      <td><a href={href(`/hierarchy/users/${row.user_id}`)}>{row.name || row.email}</a><div className="hint">{row.email} · {row.role}</div></td>
      <td>{row.attachment ? <>{row.attachment.level}{row.attachment.ref_name ? `: ${row.attachment.ref_name}` : ''}</> : <span className="hint">not attached</span>}</td>
      <td><NotebookPill nb={row.user_notebook} /></td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <select aria-label={`Attach ${row.email}`} value={target} onChange={(e) => setTarget(e.target.value)}>
          <option value="">Attach to…</option>
          <option value="corporate:">Corporate</option>
          {(tree.teams ?? []).map((t) => <option key={t.id} value={`team:${t.id}`}>Team: {t.name}</option>)}
          {projects.map((p) => <option key={p.id} value={`project:${p.id}`}>Project: {p.name}</option>)}
        </select>{' '}
        <button className="btn sm" disabled={busy || !target || target === current} onClick={attach}>Attach</button>{' '}
        {row.attachment && <button className="btn ghost sm" disabled={busy} onClick={detach}>Detach</button>}
      </td>
    </tr>
  )
}

export default function Hierarchy({ session }: { session: Session }) {
  const token = session.token
  const editable = canEditHierarchy(session.user.role)
  const [tree, setTree] = useState<LevelTree | null>(null)
  const [people, setPeople] = useState<PersonRow[] | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    const [t, p] = await Promise.all([api<LevelTree>('/levels/tree', {}, token), api<{ items: PersonRow[] }>('/levels/people', {}, token)])
    if (isOk(t)) { setTree(t.data); setLoadErr(null) } else setLoadErr(levelErrorText(t.error))
    setPeople(isOk(p) ? p.data.items : [])
  }, [token])
  useEffect(() => { load() }, [load])

  const done = (msg: string | null) => { setError(null); setNotice(msg); load() }

  async function createTeam(e: FormEvent) {
    e.preventDefault()
    setBusy(true); setError(null); setNotice(null)
    const r = await api<unknown>('/levels/teams', { method: 'POST', body: JSON.stringify({ name: name.trim() }) }, token)
    setBusy(false)
    if (!isOk(r)) { setError(levelErrorText(r.error)); return }
    setName(''); done(`Created team ${name.trim()}.`)
  }
  async function move(teamId: string, projectId: string) {
    setError(null); setNotice(null)
    const r = await api<unknown>(`/levels/teams/${teamId}/projects/${projectId}`, { method: 'PUT' }, token)
    if (!isOk(r)) setError(levelErrorText(r.error)); else done('Moved. That project’s effective Notebooks are marked stale; re-birth them on its page.')
  }
  async function remove(teamId: string, projectId: string) {
    setError(null); setNotice(null)
    const r = await api<unknown>(`/levels/teams/${teamId}/projects/${projectId}`, { method: 'DELETE' }, token)
    if (!isOk(r)) setError(levelErrorText(r.error)); else done('The project now inherits straight from Corporate.')
  }
  async function deleteTeam(id: string) {
    setError(null); setNotice(null)
    const r = await api<unknown>(`/levels/teams/${id}`, { method: 'DELETE' }, token)
    if (!isOk(r)) setError(levelErrorText(r.error)); else done('Team deleted.')
  }

  if (loadErr) return <div className="hierarchy"><p className="err" role="alert">{loadErr}</p></div>
  if (!tree) return <div className="hierarchy"><p className="hint">Loading…</p></div>

  return (
    <div className="hierarchy">
      <h2>Hierarchy</h2>
      <p className="hint">Canon → Corporate → Teams → Projects. A project is in at most one team; with none it inherits straight from Corporate. Each person is attached at one node.</p>
      {error && <p className="err" role="alert">{error}</p>}
      {notice && <p className="hint" role="status">{notice}</p>}

      <div className="panel">
        <ul className="tree">
          <li className="node corporate">
            <div className="head"><a href={href('/hierarchy/corporate')}><b>Corporate</b></a> <NotebookPill nb={tree.corporate?.notebook ?? null} /></div>
            <div className="people"><People people={tree.corporate?.people} /></div>
            <ul>
              {(tree.teams ?? []).length === 0 && <li className="hint">No teams yet.</li>}
              {(tree.teams ?? []).map((t) => (
                <li key={t.id} className="node team">
                  <div className="head">
                    <a href={href(`/hierarchy/teams/${t.id}`)}><b>Team: {t.name}</b></a> <NotebookPill nb={t.notebook} />
                    {editable && (t.projects ?? []).length === 0 && (t.people ?? []).length === 0 && <span className="acts"><button className="btn ghost sm" onClick={() => deleteTeam(t.id)}>Delete team</button></span>}
                  </div>
                  <div className="people"><People people={t.people} /></div>
                  <ul>
                    {(t.projects ?? []).length === 0 && <li className="hint">No projects in this team.</li>}
                    {(t.projects ?? []).map((p) => <ProjectRow key={p.id} p={p} teams={tree.teams ?? []} teamId={t.id} editable={editable} onMove={move} onRemove={remove} />)}
                  </ul>
                </li>
              ))}
              <li className="node team none">
                <div className="head"><b>No team</b> <span className="hint">(inherit straight from Corporate)</span></div>
                <ul>
                  {(tree.unassigned_projects ?? []).length === 0 && <li className="hint">Every project is in a team.</li>}
                  {(tree.unassigned_projects ?? []).map((p) => <ProjectRow key={p.id} p={p} teams={tree.teams ?? []} teamId={null} editable={editable} onMove={move} onRemove={remove} />)}
                </ul>
              </li>
            </ul>
          </li>
        </ul>
      </div>

      {editable && (
        <form className="panel" onSubmit={createTeam}>
          <h3 style={{ marginTop: 0 }}>Create a team</h3>
          <div className="row">
            <input aria-label="Team name" placeholder="Team name" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
            <button className="btn" disabled={busy || !name.trim()}>{busy ? 'Creating…' : 'Create team'}</button>
          </div>
        </form>
      )}

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>People</h3>
        <table>
          <thead><tr><th>Person</th><th>Attached to</th><th>User Notebook</th><th></th></tr></thead>
          <tbody>
            {people === null && <tr><td colSpan={4} className="hint">Loading…</td></tr>}
            {people && people.length === 0 && <tr><td colSpan={4} className="hint">No people.</td></tr>}
            {people?.map((r) => editable
              ? <AttachRow key={r.user_id} row={r} tree={tree} token={token} onChanged={() => done('Saved. That person’s effective Notebooks are marked stale.')} onError={setError} />
              : (
                <tr key={r.user_id}>
                  <td><a href={href(`/hierarchy/users/${r.user_id}`)}>{r.name || r.email}</a><div className="hint">{r.email} · {r.role}</div></td>
                  <td>{r.attachment ? <>{r.attachment.level}{r.attachment.ref_name ? `: ${r.attachment.ref_name}` : ''}</> : <span className="hint">not attached</span>}</td>
                  <td><NotebookPill nb={r.user_notebook} /></td><td></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
